import { useSearchParams } from 'react-router-dom'
import { groupBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { CdtCodeMappingForm } from 'app/components/mapping/CdtCodeMappingForm'
import type { CdtCodeMappingType } from 'app/types/entities'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { useHandleSearchParams, useWorkspace } from 'app/utils'
import { defaultCdtRanges, treatmentCategories, type TreatmentCategoryValue } from 'lib/cdtCodeDefaults'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { useCdtCodeMappingStore } from 'app/stores/StoreContext'

export function formatRange(category: TreatmentCategoryValue): string {
  const range = defaultCdtRanges.find(([, , cat]) => cat === category)
  if (!range) return ``
  return `D${String(range[0]).padStart(4, `0`)}–D${String(range[1]).padStart(4, `0`)}`
}

export type UseCdtCodeMappingReturn = {
  loading: boolean
  filteredCategories: TreatmentCategoryValue[]
  groupedMappings: Record<string, CdtCodeMappingType[]>
  categoriesWithMappings: Set<string>
  canEditMapping: boolean
  onNewMapping: () => void
  onEditMapping: (mapping: CdtCodeMappingType) => void
  onDeleteMapping: (id: string) => Promise<void>
  savingIds: Set<string>
  disclosureKey: number
  onSearch: (query: string) => void
  searchValue: string
}

// Callers must be observer() components — the hook reads the MobX store's list.
export function useCdtCodeMapping(): UseCdtCodeMappingReturn {
  const [confirm] = useConfirmation()
  const store = useCdtCodeMappingStore()
  const workspace = useWorkspace()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const [disclosureKey, setDisclosureKey] = useState(0)
  const [optimisticAdds, setOptimisticAdds] = useState<CdtCodeMappingType[]>([])
  const [optimisticEdits, setOptimisticEdits] = useState<Map<string, Partial<CdtCodeMappingType>>>(new Map())
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (workspace?.id) bg(store.ensureLoaded(workspace.id))
  }, [store, workspace?.id])

  const serverList = store.list
  const loading = !!workspace?.id && serverList == null

  const allMappings = useMemo(() => {
    const serverHits = (serverList?.hits ?? [])
      .filter((m) => !deletedIds.has(m.id!))
      .map((m) => {
        const edit = optimisticEdits.get(m.id!)
        return edit ? { ...m, ...edit } : m
      }) as CdtCodeMappingType[]
    return [...serverHits, ...optimisticAdds]
  }, [serverList, deletedIds, optimisticEdits, optimisticAdds])

  const groupedMappings = useMemo(() => groupBy(allMappings, `category`), [allMappings])
  const categoriesWithMappings = useMemo(() => new Set(Object.keys(groupedMappings)), [groupedMappings])

  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const searchValue = searchParams.get(`query`) ?? ``
  const query = searchValue.toLowerCase() || undefined
  const onSearch = useCallback(
    (value: string) => {
      handleSearchParams({ query: value || undefined })
    },
    [handleSearchParams],
  )

  const filteredCategories = useMemo(() => {
    if (!query) return treatmentCategories
    return treatmentCategories.filter(
      (category) =>
        category.toLowerCase().includes(query) ||
        formatRange(category).toLowerCase().includes(query) ||
        groupedMappings[category]?.some((m) => m.cdtCode?.toLowerCase().includes(query)),
    )
  }, [query, groupedMappings])

  const handleSave = useCallback(
    async (data: { cdtCode: string; category: string }, mappingId?: string) => {
      if (!workspace) return
      const isEdit = !!mappingId

      if (isEdit) {
        setSavingIds((prev) => new Set(prev).add(mappingId))
        setOptimisticEdits((prev) => new Map(prev).set(mappingId, { cdtCode: data.cdtCode, category: data.category }))
      } else {
        const tempId = `temp-${Date.now()}`
        setOptimisticAdds((prev) => [
          ...prev,
          { id: tempId, cdtCode: data.cdtCode, category: data.category } as CdtCodeMappingType,
        ])
      }
      setDisclosureKey((k) => k + 1)

      // orgId/workspaceId are derived server-side from the session; the API
      // rejects them as input fields.
      let result
      if (isEdit) {
        result = await store.update(mappingId, {
          cdtCode: data.cdtCode,
          category: data.category,
        })
      } else {
        result = await store.create({
          cdtCode: data.cdtCode,
          category: data.category,
        })
      }
      if (result.error) {
        notifyError(`Error saving CDT code mapping`)
      } else {
        const saved = isEdit ? result.data?.updateCdtCodeMapping : result.data?.createCdtCodeMapping
        if (saved?.id) {
          store.announceChange({
            workspaceId: workspace.id,
            op: isEdit ? `update` : `create`,
            mapping: {
              id: saved.id,
              cdtCode: saved.cdtCode ?? undefined,
              category: saved.category ?? undefined,
              updatedDate: saved.updatedDate ?? undefined,
            },
          })
        }
      }
    },
    [workspace, store],
  )

  const handleDelete = useCallback(
    async (id: string): Promise<void> => {
      if (!workspace) return
      setDeletedIds((prev) => new Set(prev).add(id))
      setDisclosureKey((k) => k + 1)

      const result = await store.remove(id)
      if (result.error) {
        logError(`Error deleting CDT code mapping`, result.error)
        notifyError(`Error deleting CDT code mapping`)
        setDeletedIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
        setDisclosureKey((k) => k + 1)
      } else {
        const removed = allMappings.find((m) => m.id === id)
        store.announceChange({
          workspaceId: workspace.id,
          op: `remove`,
          mapping: { id, cdtCode: removed?.cdtCode, category: removed?.category },
        })
      }
    },
    [workspace, store, allMappings],
  )

  const handleTableDelete = async (id: string): Promise<void> => {
    await confirm({
      title: `Delete CDT Code Mapping`,
      description: `Are you sure you want to delete this CDT code mapping?`,
      variant: `danger`,
    }).then(() => handleDelete(id))
  }

  const openModal = ({ title, mapping }: { title: string; mapping?: CdtCodeMappingType }) =>
    showModal(
      <CdtCodeMappingForm
        mapping={mapping}
        existingMappings={allMappings}
        onSave={handleSave}
        onDelete={handleDelete}
      />,
      { title, isFullScreen: false, removePadding: true, useRadix: true },
    )

  // Clear optimistic state after the store's list refreshes (mutations replace
  // store.list with the server-confirmed rows).
  useEffect(() => {
    if (deletedIds.size > 0 || optimisticAdds.length > 0 || optimisticEdits.size > 0 || savingIds.size > 0) {
      setDeletedIds(new Set())
      setOptimisticAdds([])
      setOptimisticEdits(new Map())
      setSavingIds(new Set())
      setDisclosureKey((k) => k + 1)
    }
  }, [serverList])

  return {
    loading,
    filteredCategories,
    groupedMappings,
    categoriesWithMappings,
    canEditMapping,
    onNewMapping: () => openModal({ title: `Create New CDT Code Mapping` }),
    onEditMapping: (mapping) => openModal({ title: `Edit CDT Code Mapping`, mapping }),
    onDeleteMapping: handleTableDelete,
    savingIds,
    disclosureKey,
    onSearch,
    searchValue,
  }
}
